import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, 
  HelpCircle, 
  MessageSquare, 
  Send, 
  Loader2,
  BookOpen,
  FileText,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Plus,
  ArrowLeft,
  User,
  Shield,
  Paperclip,
  Image,
  X,
  Upload,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const SupportPage = () => {
  const { api, user } = useAuth();
  const isAdmin = user?.user_type === 'super_admin' || user?.is_super_admin || user?.roles?.some(r => r.name === 'Admin');
  const [activeTab, setActiveTab] = useState('knowledge-base');
  const [loading, setLoading] = useState(false);
  
  // Knowledge Base state
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicketForm, setNewTicketForm] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    searchKnowledgeBase();
    fetchTickets();
  }, []);

  useEffect(() => {
    searchKnowledgeBase();
  }, [searchQuery, selectedCategory]);

  const searchKnowledgeBase = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await api().get(`/support/knowledge-base?${params}`);
      setArticles(response.data.articles || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to search knowledge base');
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await api().get('/support/tickets');
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets');
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicketForm.subject.trim() || !newTicketForm.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api().post('/support/tickets', {
        ...newTicketForm,
        attachments: ticketAttachments
      });
      toast.success(response.data.message);
      setShowNewTicket(false);
      setNewTicketForm({ subject: '', message: '', priority: 'normal' });
      setTicketAttachments([]);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyTicket = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSubmitting(true);
    try {
      await api().post(`/support/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage,
        attachments: replyAttachments
      });
      toast.success('Reply sent');
      setReplyMessage('');
      setReplyAttachments([]);
      
      const response = await api().get(`/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(response.data);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e, target) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB.');
      return;
    }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api().post('/support/tickets/upload-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const attachment = response.data;
      if (target === 'ticket') {
        setTicketAttachments(prev => [...prev, attachment]);
      } else {
        setReplyAttachments(prev => [...prev, attachment]);
      }
      toast.success('File attached');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api().patch(`/support/tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Ticket marked as ${newStatus}`);
      const response = await api().get(`/support/tickets/${ticketId}`);
      setSelectedTicket(response.data);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { variant: 'default', icon: AlertCircle, label: 'Open' },
      in_progress: { variant: 'secondary', icon: Clock, label: 'In Progress' },
      fixed: { variant: 'outline', icon: CheckCircle, label: 'Fixed', className: 'border-blue-500 text-blue-600' },
      resolved: { variant: 'outline', icon: CheckCircle, label: 'Resolved', className: 'border-green-500 text-green-600' },
      closed: { variant: 'secondary', icon: XCircle, label: 'Closed' }
    };
    const style = styles[status] || styles.open;
    const Icon = style.icon;
    
    return (
      <Badge variant={style.variant} className={`flex items-center gap-1 ${style.className || ''}`}>
        <Icon className="w-3 h-3" />
        {style.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      normal: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      high: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
      urgent: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
    };
    return (
      <Badge className={colors[priority] || colors.normal}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="support-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <HelpCircle className="w-8 h-8 text-primary" />
          Help & Support
        </h1>
        <p className="text-muted-foreground mt-1">Find answers or contact our support team</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="knowledge-base" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Knowledge Base
          </TabsTrigger>
          <TabsTrigger value="tickets" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Support Tickets
            {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge-base" className="space-y-4">
          {selectedArticle ? (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedArticle(null)}>
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                  <Badge variant="outline">{selectedArticle.category}</Badge>
                </div>
                <CardTitle>{selectedArticle.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose dark:prose-invert max-w-none">
                  <ReactMarkdown>{selectedArticle.content}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Search */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search help articles... (e.g., 'how to add university', 'credits')"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                      <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Articles */}
              <div className="grid gap-3">
                {articles.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p className="text-muted-foreground">No articles found. Try a different search term.</p>
                    </CardContent>
                  </Card>
                ) : (
                  articles.map(article => (
                    <Card 
                      key={article.id} 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setSelectedArticle(article)}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <FileText className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-medium">{article.title}</h3>
                              <p className="text-sm text-muted-foreground mt-1">
                                {article.content.substring(0, 100)}...
                              </p>
                              <Badge variant="outline" className="mt-2">{article.category}</Badge>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets" className="space-y-4">
          {selectedTicket ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)}>
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedTicket.status)}
                    {getPriorityBadge(selectedTicket.priority)}
                    {isAdmin && (
                      <Select value={selectedTicket.status} onValueChange={(val) => handleStatusChange(selectedTicket.id, val)}>
                        <SelectTrigger className="w-[200px] h-8 text-sm" data-testid="admin-status-change">
                          <SelectValue placeholder="Change Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="fixed">Fixed (Awaiting Confirmation)</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                <CardTitle className="mt-2">{selectedTicket.subject}</CardTitle>
                <CardDescription className="flex items-center gap-2 flex-wrap mt-1">
                  <span>
                    {selectedTicket.serial_number != null && (
                      <span className="inline-flex items-center px-1.5 py-0.5 mr-2 rounded-md bg-amber-50 dark:bg-slate-900/30 border border-amber-200 dark:border-slate-800 text-slate-800 dark:text-amber-300 text-[10px] font-bold">
                        Sr. #{selectedTicket.serial_number}
                      </span>
                    )}
                    Ticket #{selectedTicket.ticket_number}
                  </span>
                  <span className="text-xs">• Created {new Date(selectedTicket.created_at).toLocaleDateString()}</span>
                  {selectedTicket.user_name && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-slate-900/40 text-slate-800 dark:text-amber-300 text-xs font-medium" data-testid="ticket-detail-issuer">
                      <User className="w-3 h-3" />
                      Issued by {selectedTicket.user_name}
                    </span>
                  )}
                  {selectedTicket.reopen_count > 0 && (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-950/30">
                      Reopened ×{selectedTicket.reopen_count}
                    </Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Original Message */}
                <div className="p-4 rounded-lg bg-muted">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-slate-800 dark:text-amber-300" />
                    <span className="font-semibold text-slate-800 dark:text-amber-300" data-testid="ticket-issuer-name">
                      {selectedTicket.user_name || 'You'}
                    </span>
                    {selectedTicket.user_email && (
                      <span className="text-xs text-muted-foreground">({selectedTicket.user_email})</span>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {new Date(selectedTicket.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap">{selectedTicket.message}</p>
                  {selectedTicket.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {selectedTicket.attachments.map((att, i) => (
                        <a key={i} href={`${api().defaults.baseURL}/support/tickets/attachment/${att.file_id}`} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background border text-sm hover:bg-muted transition-colors">
                          <Paperclip className="w-3.5 h-3.5" />
                          {att.file_name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* User action buttons when ticket is fixed */}
                {selectedTicket.status === 'fixed' && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-800 dark:text-blue-300">Support marked this as fixed</p>
                      <p className="text-sm text-blue-600 dark:text-blue-400">Please verify and confirm, or reopen if the issue persists.</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleStatusChange(selectedTicket.id, 'open')} data-testid="reopen-fixed-btn">
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reopen
                      </Button>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleStatusChange(selectedTicket.id, 'resolved')}>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Confirm Resolved
                      </Button>
                    </div>
                  </div>
                )}

                {/* Issuer can reopen closed/resolved tickets at any time */}
                {(selectedTicket.status === 'closed' || selectedTicket.status === 'resolved')
                  && selectedTicket.user_id === user?.id && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-800 dark:text-amber-300">
                        This ticket is {selectedTicket.status}.
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        Issue still occurring? You can reopen this ticket at any time.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-400 text-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900"
                      onClick={() => handleStatusChange(selectedTicket.id, 'open')}
                      data-testid="reopen-closed-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reopen Ticket
                    </Button>
                  </div>
                )}

                {/* Replies */}
                {selectedTicket.replies?.map((reply, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg ${reply.is_admin ? 'bg-primary/10 border border-primary/20' : 'bg-muted'}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {reply.is_admin ? (
                        <Shield className="w-4 h-4 text-primary" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span className="font-medium">
                        {reply.is_admin ? 'Support Team' : reply.user_name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(reply.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap">{reply.message}</p>
                    {reply.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {reply.attachments.map((att, i) => (
                          <a key={i} href={`${api().defaults.baseURL}/support/tickets/attachment/${att.file_id}`} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background border text-sm hover:bg-muted transition-colors">
                            <Paperclip className="w-3.5 h-3.5" />
                            {att.file_name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Reply Form */}
                {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label>Add Reply</Label>
                    <Textarea
                      placeholder="Type your message..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      rows={3}
                    />
                    {replyAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {replyAttachments.map((att, i) => (
                          <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded bg-muted text-sm">
                            <Paperclip className="w-3 h-3" />
                            <span className="max-w-[120px] truncate">{att.file_name}</span>
                            <button onClick={() => setReplyAttachments(prev => prev.filter((_, idx) => idx !== i))} className="ml-1 hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer">
                        <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx" onChange={(e) => handleFileUpload(e, 'reply')} />
                        <Button variant="outline" size="sm" type="button" asChild disabled={uploadingFile}>
                          <span>
                            {uploadingFile ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1" />}
                            Attach File
                          </span>
                        </Button>
                      </label>
                      <Button onClick={handleReplyTicket} disabled={submitting || !replyMessage.trim()}>
                        {submitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Reply
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* New Ticket Button */}
              <div className="flex justify-between items-center">
                <p className="text-muted-foreground">
                  {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                </p>
                <Button onClick={() => setShowNewTicket(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Ticket
                </Button>
              </div>

              {/* Tickets List */}
              {tickets.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p className="text-muted-foreground mb-4">No support tickets yet</p>
                    <Button onClick={() => setShowNewTicket(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Ticket
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {tickets.map(ticket => (
                    <Card 
                      key={ticket.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setSelectedTicket(ticket)}
                      data-testid={`ticket-card-${ticket.id}`}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-stretch gap-3">
                          {/* Serial number column — bold, easy to dictate */}
                          <div className="flex flex-col items-center justify-center min-w-[60px] px-3 rounded-md bg-amber-50 dark:bg-slate-900/30 border border-amber-200 dark:border-slate-800">
                            <div className="text-[10px] uppercase tracking-wide text-slate-800 dark:text-amber-300 font-medium">Sr.</div>
                            <div className="text-xl font-bold text-slate-800 dark:text-amber-300 leading-tight" data-testid={`ticket-serial-${ticket.id}`}>
                              {ticket.serial_number ?? '—'}
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs text-muted-foreground font-mono" data-testid={`ticket-number-${ticket.id}`}>
                                {ticket.ticket_number}
                              </span>
                              {getStatusBadge(ticket.status)}
                              {getPriorityBadge(ticket.priority)}
                              {ticket.reopen_count > 0 && (
                                <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-950/30">
                                  Reopened ×{ticket.reopen_count}
                                </Badge>
                              )}
                            </div>
                            <h3 className="font-medium truncate">{ticket.subject}</h3>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                              {ticket.message?.substring(0, 100)}{ticket.message?.length > 100 ? '…' : ''}
                            </p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground flex-wrap">
                              {/* Highlighted issuer chip — pulls user_name stamped at creation time */}
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-slate-900/40 text-slate-800 dark:text-amber-300 font-medium" data-testid={`ticket-issuer-${ticket.id}`}>
                                <User className="w-3 h-3" />
                                {ticket.user_name || ticket.created_by_name || 'Unknown'}
                              </span>
                              <span>•</span>
                              <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                              <span>•</span>
                              <span>{ticket.replies?.length || 0} replies</span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground self-center" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>
              Describe your issue and our team will respond within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                placeholder="Brief description of your issue"
                value={newTicketForm.subject}
                onChange={(e) => setNewTicketForm(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select 
                value={newTicketForm.priority} 
                onValueChange={(v) => setNewTicketForm(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - General question</SelectItem>
                  <SelectItem value="normal">Normal - Need help</SelectItem>
                  <SelectItem value="high">High - Affecting work</SelectItem>
                  <SelectItem value="urgent">Urgent - Critical issue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Please describe your issue in detail..."
                value={newTicketForm.message}
                onChange={(e) => setNewTicketForm(prev => ({ ...prev, message: e.target.value }))}
                rows={5}
              />
            </div>
            <div className="space-y-2">
              <Label>Attachments</Label>
              {ticketAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {ticketAttachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded bg-muted text-sm">
                      <Paperclip className="w-3 h-3" />
                      <span className="max-w-[150px] truncate">{att.file_name}</span>
                      <button onClick={() => setTicketAttachments(prev => prev.filter((_, idx) => idx !== i))} className="ml-1 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="cursor-pointer inline-block">
                <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx" onChange={(e) => handleFileUpload(e, 'ticket')} />
                <Button variant="outline" size="sm" type="button" asChild disabled={uploadingFile}>
                  <span>
                    {uploadingFile ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1" />}
                    {uploadingFile ? 'Uploading...' : 'Attach File'}
                  </span>
                </Button>
              </label>
              <p className="text-xs text-muted-foreground">Images, PDFs, and documents up to 10MB</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTicket(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportPage;
